import * as Sentry from '@sentry/react';
import type {
  GamePlayerProjectionsRequest,
  Projection,
  ProjectionsAndScoresAPIResponse
} from '@library/types/projection';
import { concat, mergeDeepWith } from 'ramda';
import {
  createProjectionsWebSocket,
  getProjectionsFromWebSocket
} from '@bo-utils/projection';
import {
  getPlayersProjectionByGame,
  getTradingProjection,
  isRelevantWSMessage
} from '@bo-utils/trading';
import type { TradingData } from '@library/types/trading';
import { api } from '@bo-services/api';
import { apiPaths } from '@bo-constants/services';
import { hasStandAloneOwnAttributes } from '@bo-utils/leagueAttributes';
import { sortByStatType } from '@bo-utils/projectionsByStatType';

const { admin } = apiPaths;

export const tradingApi = api.injectEndpoints({
  endpoints: (build) => ({
    getProjections: build.query<TradingData, GamePlayerProjectionsRequest>({
      query: ({
        createdByUserId,
        demonsAndGoblins = undefined,
        durationId,
        fromDate,
        gameId = undefined,
        gameModeIds = undefined,
        inGame = undefined,
        leagueId,
        page = 1,
        perPage = 300,
        playerIds = undefined,
        search = undefined,
        statTypeIds = undefined,
        status = undefined,
        team,
        toDate = undefined
      }) => ({
        url: `${admin}projections`,
        method: 'GET',
        params: {
          demons_and_goblins: demonsAndGoblins,
          created_by_user_id: createdByUserId,
          duration_id: durationId,
          from_date: fromDate,
          to_date: toDate,
          game_ids: gameId,
          game_mode_ids: gameModeIds,
          in_game: inGame,
          league_id: leagueId,
          new_player_ids: playerIds,
          page,
          per_page: perPage,
          search,
          stat_type_ids: statTypeIds,
          statuses: status,
          teams: team
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        try {
          await cacheDataLoaded;

          const ws = createProjectionsWebSocket(arg.leagueId ?? []);

          const listener = (event: MessageEvent) => {
            const { projections, included } =
              getProjectionsFromWebSocket(event);
            if (!projections.length || !included) return;

            projections.forEach((dataItem: Projection<string>) => {
              if (
                !isRelevantWSMessage({
                  arg,
                  projectionData: { data: dataItem, included }
                })
              ) {
                return;
              }

              const streamedProjection = getTradingProjection({
                projection: dataItem,
                included
              });

              const {
                description,
                durationId,
                gameId,
                leagueId,
                playerId = '',
                startTime,
                team,
                teamName
              } = streamedProjection;

              const leagueData = included.find(
                (include) =>
                  include.type === 'league' &&
                  dataItem?.relationships?.league?.data?.id === include.id
              );

              const {
                attributes: { name: leagueName }
              } = leagueData ?? { attributes: { name: '' } };

              const durationOrLeague = hasStandAloneOwnAttributes({
                name: leagueName
              });
              const durationString = durationOrLeague ? durationId : leagueId;
              const teamIdOrName = teamName || team || '';

              const determineNLSGameId = (
                knownGames: Array<string>
              ): string => {
                const descriptionThenTeam = `NLS-${description}-${team ?? ''}-${startTime}`;
                if (knownGames.includes(descriptionThenTeam)) {
                  return descriptionThenTeam;
                }
                const teamThenDescription = `NLS-${team ?? ''}-${description}-${startTime}`;
                return teamThenDescription;
              };

              updateCachedData((draft) => {
                const { tradingGames } = draft;
                const gameString = gameId
                  ? String(gameId)
                  : determineNLSGameId(Object.keys(tradingGames));
                const gameIdentifier = `${gameString} duration-${durationString ?? ''}`;
                const targetedPlayer =
                  tradingGames?.[gameIdentifier]?.[teamIdOrName]?.[playerId];

                if (!targetedPlayer) {
                  if (!tradingGames[gameIdentifier]) {
                    tradingGames[gameIdentifier] = {};
                  }
                  if (!tradingGames[gameIdentifier][teamIdOrName]) {
                    tradingGames[gameIdentifier][teamIdOrName] = {};
                  }

                  if (!tradingGames[gameIdentifier][teamIdOrName][playerId]) {
                    tradingGames[gameIdentifier][teamIdOrName][playerId] = [];
                  }

                  tradingGames[gameIdentifier][teamIdOrName][playerId].push(
                    streamedProjection
                  );
                  return draft;
                }

                const projectionIndex = targetedPlayer.findIndex(
                  (projection) =>
                    projection.projectionId === streamedProjection.projectionId
                );

                if (projectionIndex === -1) {
                  targetedPlayer.push(streamedProjection);
                } else {
                  targetedPlayer[projectionIndex] = streamedProjection;
                }

                return draft;
              });
            });
          };

          ws.addEventListener('message', listener);

          await cacheEntryRemoved;
          ws.removeEventListener('message', listener);
          ws.close();
        } catch (error) {
          Sentry.captureException(error, {
            tags: { source: 'tradingApi.getProjections WebSocket' },
            extra: { message: 'Trading Dash Websocket Error' }
          });

          // eslint-disable-next-line no-console
          console.log('Trading Dash Websocket Error', error);
        }
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { page, ...rest } = queryArgs;
        return `${endpointName}${Object.entries(rest).flat(1).join('')}`;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page === 1) return newItems;

        return mergeDeepWith(
          (a: TradingData, b: TradingData) => {
            if (Array.isArray(a) && Array.isArray(b)) {
              return sortByStatType(concat(a, b) as Array<TradingData>);
            }
            return b;
          },
          currentCache,
          newItems
        ) as TradingData;
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        JSON.stringify(currentArg) !== JSON.stringify(previousArg),
      keepUnusedDataFor: 0,
      transformResponse: (
        response: ProjectionsAndScoresAPIResponse,
        _,
        { perPage }
      ) => getPlayersProjectionByGame({ perPage, projections: response }),
      providesTags: ['Projections']
    })
  })
});

export const { useGetProjectionsQuery } = tradingApi;
